import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { collection, query, where, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Book, Play, PenTool, Plus, Clock, ChevronRight } from 'lucide-react';
import { vibrate, HapticPatterns } from '../../utils/haptics';
import { Post } from './Feed';
import { getDailyWord } from '../../data/wordList';

const isNativeApp = Capacitor.isNativePlatform();

interface ModernFeedProps {}

export default function ModernFeed(props: ModernFeedProps) {
  const { user } = useAuth();

  // states
  const [recentBits, setRecentBits] = useState<any[]>([]);
  const [username, setUsername] = useState<string>('');
  const [stats, setStats] = useState({ bits: 0, ideas: 0, sets: 0, streak: 0 });
  const [quickIdea, setQuickIdea] = useState('');
  const [savingIdea, setSavingIdea] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      const draftsQ = query(collection(db, 'drafts'), where('userId', '==', user.uid));
      const ideasQ = query(collection(db, 'ideas'), where('userId', '==', user.uid));
      const setlistsQ = query(collection(db, 'setlists'), where('userId', '==', user.uid));
      const exerciseSavesQ = query(collection(db, 'exerciseSaves'), where('userId', '==', user.uid));

      try {
        const [draftsSnap, ideasSnap, setlistsSnap, userDoc, exerciseSavesSnap] = await Promise.all([
          getDocs(draftsQ), getDocs(ideasQ), getDocs(setlistsQ),
          getDoc(doc(db, 'users', user.uid)),
          getDocs(exerciseSavesQ),
        ]);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUsername(userData.username || '');
        }

        // Calculate daily-word streak
        const todayET = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
        const doneDates = new Set(
          exerciseSavesSnap.docs
            .map(d => d.data())
            .filter(d => d.type === 'daily-word' && d.date)
            .map(d => d.date as string)
        );

        let streak = 0;
        const cursor = new Date(todayET + 'T12:00:00'); // noon to avoid DST edge cases
        if (!doneDates.has(todayET)) cursor.setDate(cursor.getDate() - 1); // haven't done today yet — start from yesterday
        while (true) {
          const dateStr = new Intl.DateTimeFormat('en-CA').format(cursor);
          if (doneDates.has(dateStr)) {
            streak++;
            cursor.setDate(cursor.getDate() - 1);
          } else {
            break;
          }
        }

        setStats({
          bits: draftsSnap.size,
          ideas: ideasSnap.size,
          sets: setlistsSnap.size,
          streak,
        });

        // Get top 3 recent bits sorted by updatedAt or createdAt
        const bits = draftsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        bits.sort((a: any, b: any) => {
          const aDate = a.updatedAt || a.createdAt || '';
          const bDate = b.updatedAt || b.createdAt || '';
          return bDate.localeCompare(aDate);
        });
        setRecentBits(bits.slice(0, 3));

      } catch (err) {
        console.error("Error fetching dashboard data", err);
      }
    };
    fetchData();
  }, [user?.uid]);

  const handleQuickIdea = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickIdea.trim() || !user || savingIdea) return;
    setSavingIdea(true);
    try {
      const now = new Date().toISOString();
      await addDoc(collection(db, 'ideas'), {
        userId: user.uid,
        title: '',
        description: quickIdea.trim(),
        tags: ['quick-capture'],
        createdAt: now,
        updatedAt: now
      });
      setQuickIdea('');
      vibrate(HapticPatterns.SUCCESS);
      setStats(s => ({...s, ideas: s.ideas + 1}));
    } catch(err) {
      console.error(err);
    } finally {
      setSavingIdea(false);
    }
  };

  const todaysWord = getDailyWord();

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const displayName = username || user?.displayName || '';

  const exerciseSection = (
    <>
      <h2 className="text-lg font-extrabold text-gray-900 dark:text-white flex items-center gap-2 px-1">
        Today's Drill
      </h2>
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl p-6 sm:p-8 text-white shadow-md relative overflow-hidden group hover:shadow-lg transition-all flex flex-col justify-between min-h-[260px]">
        <div className="absolute -right-4 -top-4 opacity-20 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
          <PenTool size={140} />
        </div>
        <div>
          <span className="bg-black/30 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4 inline-block border border-white/20">
            Today's Exercise
          </span>
          <p className="text-orange-100 text-sm font-semibold mb-1 relative z-10">Write a joke using the word</p>
          <h3 className="text-4xl font-black capitalize tracking-tight relative z-10">{todaysWord}</h3>
        </div>
        <Link to="/exercise/daily-word" className="mt-6 bg-white text-orange-600 hover:bg-orange-50 px-6 py-3 rounded-xl font-bold transition-colors w-full text-center shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center gap-2 relative z-10">
          <Play size={16} fill="currentColor" />
          Start
        </Link>
      </div>
    </>
  );

  return (
    <div className="modern-feed-container max-w-5xl mx-auto space-y-6 pb-24 pt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header / Hero */}
      <div className="mb-8 px-2">
        <p className="text-orange-500 text-sm font-bold uppercase tracking-widest mt-8 mb-6 block">Your Dashboard</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">
          {greeting}{displayName ? `, ${displayName}` : ''}.
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-base mt-2 font-medium">
          {stats.bits > 0
            ? `You've got ${stats.bits} bit${stats.bits === 1 ? '' : 's'} in the notebook.`
            : 'Your notebook is empty. Start writing.'}
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Bits Written', value: stats.bits, to: '/write' },
          { label: 'Ideas', value: stats.ideas, to: '/ideas' },
          { label: 'Set Lists', value: stats.sets, to: '/setlist' },
          { label: 'Exercise Streak', value: stats.streak === 0 ? '—' : stats.streak, to: '/exercises' },
        ].map((stat, i) => (
          <Link key={i} to={stat.to} className="bg-white dark:bg-navy-800 rounded-2xl p-5 shadow border border-gray-200 dark:border-navy-700 flex flex-col items-center justify-center text-center hover:border-orange-300 dark:hover:border-orange-500/40 hover:shadow-md transition-all active:scale-95">
            <span className="text-2xl font-extrabold text-gray-900 dark:text-white leading-none mb-1">{stat.value}</span>
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{stat.label}</span>
          </Link>
        ))}
      </div>

      {/* Today's Exercise (apps only — kept just under the content cards so it doesn't require scrolling) */}
      {isNativeApp && (
        <div className="space-y-4">
          {exerciseSection}
        </div>
      )}

      {/* Facebook CTA */}
      <a
        href="https://www.facebook.com/profile.php?id=61562237301526"
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 rounded-2xl px-5 py-4 shadow-sm hover:bg-orange-100 dark:hover:bg-orange-500/20 hover:border-orange-300 hover:shadow-md transition-all group active:scale-[0.99]"
      >
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 text-left">
          <span className="font-extrabold text-gray-900 dark:text-white block sm:inline mb-1 sm:mb-0 mr-1">Share your bits:</span>
          Send your video to Show Your Bits on Facebook for a chance to be featured.
        </p>
        <span className="flex items-center gap-1.5 text-orange-500 font-bold text-sm whitespace-nowrap sm:flex-shrink-0 self-end sm:self-auto">
          Go to page <ChevronRight size={16} />
        </span>
      </a>

      {/* Main Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column: Recent Bits */}
        <div className="lg:col-span-3 space-y-4">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-lg font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
              Continue Writing
            </h2>
            <Link to="/write" className="text-sm font-bold text-orange-500 hover:text-orange-600 flex items-center gap-1 bg-orange-50 dark:bg-orange-500/10 px-3 py-1.5 rounded-full transition-colors">
              View All <ChevronRight size={14} />
            </Link>
          </div>

          <div className="space-y-3">
            {recentBits.length > 0 ? recentBits.map(bit => (
              <Link key={bit.id} to={`/write/${bit.id}`} className="block bg-white dark:bg-navy-800 rounded-2xl p-5 sm:p-6 shadow border border-gray-200 dark:border-navy-700 hover:shadow-md hover:border-orange-300 dark:hover:border-orange-500/30 transition-all group">
                <div className="flex justify-between items-start mb-2 gap-4">
                  <h3 className="font-bold text-[16px] text-gray-900 dark:text-white group-hover:text-orange-500 transition-colors line-clamp-1">{bit.title || 'Untitled Bit'}</h3>
                  <span className="text-xs font-medium text-gray-400 flex items-center gap-1.5 flex-shrink-0 bg-gray-50 dark:bg-navy-900 px-2 py-1 rounded-md">
                    <Clock size={12} />
                    {bit.updatedAt ? new Date(bit.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'Recent'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">{bit.content || 'No content yet. Click to start writing...'}</p>
              </Link>
            )) : (
              <div className="bg-white dark:bg-navy-800 rounded-2xl p-8 text-center border border-dashed border-gray-300 dark:border-navy-600">
                <div className="w-16 h-16 bg-gray-50 dark:bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Book size={24} className="text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-4 font-medium">Your notebook is empty.</p>
                <Link to="/write" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-full transition-all shadow-sm active:scale-95">
                  <Plus size={18} /> Start Writing
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Exercises (web only — apps show this higher up, see above) */}
        {!isNativeApp && (
          <div className="lg:col-span-2 space-y-4">
            {exerciseSection}
          </div>
        )}
      </div>

      {/* Quick Capture (Fixed at bottom for easy access) */}
      <div className="fixed bottom-20 md:bottom-8 left-0 right-0 px-4 md:px-0 md:max-w-3xl md:mx-auto z-40 pointer-events-none">
        <div className="bg-white dark:bg-navy-800 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] border border-gray-100 dark:border-navy-600 p-2 flex items-center gap-2 pointer-events-auto transform transition-all focus-within:-translate-y-1 focus-within:shadow-xl">
          <form onSubmit={handleQuickIdea} className="flex-1 flex items-center">
            <input
              type="text"
              value={quickIdea}
              onChange={(e) => setQuickIdea(e.target.value)}
              placeholder="Capture an idea... (add a title later)"
              className="w-full bg-transparent border-0 px-5 py-2.5 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-0 text-[15px] font-medium"
            />
            <button
              type="submit"
              disabled={!quickIdea.trim() || savingIdea}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:hover:bg-orange-500 text-white p-2.5 rounded-full ml-1 shadow-md transition-all active:scale-95 flex items-center justify-center"
            >
              <Plus size={20} />
            </button>
          </form>
        </div>
      </div>

    </div>
  );
}
